/** Schnittstelle zum Server. Der Anbieter ergibt sich aus der Adresse, der Kunde aus dem Cookie. */

export interface Theme {
  scheme?: 'light' | 'dark';
  brand?: string;
  brand2?: string;
  action?: string;
  font?: 'sans' | 'serif' | 'rounded';
  logoUrl?: string;
  logoDarkUrl?: string;
  iconUrl?: string;
}

export interface TenantInfo {
  slug: string;
  name: string;
  status: 'active' | 'suspended';
  theme: Theme;
  legal: { impressum: string | null; datenschutz: string | null };
  withdrawalMode: 'waiver' | 'guarantee';
}

export interface CourseSummary {
  slug: string;
  title: string;
  subtitle: string | null;
  kicker: string | null;
  imageUrl: string | null;
  lessonCount: number;
  durationSeconds: number;
  completedLessons: number;
  percent: number;
  lastSeenAt: string | null;
}

export interface ProductOffer {
  slug: string;
  title: string;
  priceCents: number;
  currency: string;
}

export interface CatalogCourse {
  slug: string;
  title: string;
  subtitle: string | null;
  kicker: string | null;
  imageUrl: string | null;
  lessonCount: number;
  durationSeconds: number;
  product: ProductOffer | null;
}

export interface LessonRef {
  number: number;
  title: string;
  shortTitle: string | null;
}

export interface LibraryView {
  customer: { name: string | null; email: string };
  resume: { course: CourseSummary; lesson: LessonRef; positionSeconds: number; percent: number } | null;
  mine: CourseSummary[];
  more: CatalogCourse[];
}

export interface Material {
  id: string;
  kind: 'file' | 'link';
  title: string;
  description: string | null;
  url: string;
  fileName: string | null;
  sizeBytes: number | null;
}

export interface LessonCard {
  id: string;
  number: number;
  title: string;
  shortTitle: string | null;
  speakers: string | null;
  excerpt: string | null;
  durationSeconds: number | null;
  posterUrl: string | null;
  materialCount: number;
  chapterCount: number;
  preview: boolean;
  locked: boolean;
  progress: { percent: number; completed: boolean; positionSeconds: number } | null;
}

export interface ModuleView {
  id: string;
  title: string;
  subtitle: string | null;
  locked: boolean;
  releaseAt: string | null;
  percent: number;
  lessons: LessonCard[];
}

export interface CourseView {
  course: { slug: string; title: string; subtitle: string | null; kicker: string | null; description: string | null; imageUrl: string | null; certificateEnabled: boolean };
  access: 'full' | 'locked';
  stats: { lessons: number; durationSeconds: number; materials: number; chapters: number; completed: number; percent: number };
  resume: { number: number; positionSeconds: number } | null;
  modules: ModuleView[];
  materials: Material[];
  product: ProductOffer | null;
}

export interface LessonView {
  course: { slug: string; title: string };
  lesson: {
    id: string;
    number: number;
    title: string;
    shortTitle: string | null;
    speakers: string | null;
    body: string | null;
    durationSeconds: number | null;
    chapters: { offsetSeconds: number; label: string }[];
    materials: Material[];
    video: { hlsUrl: string; posterUrl: string } | null;
    videoStatus: 'none' | 'processing' | 'ready' | 'failed';
  };
  progress: { positionSeconds: number; watchedPercent: number; completed: boolean };
  prev: LessonRef | null;
  next: LessonRef | null;
  note: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
  ) {
    super(code);
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.body ? { 'Content-Type': 'application/json' } : {}), ...init?.headers },
  });
  if (!res.ok) {
    let code = String(res.status);
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      code = body.error ?? body.message ?? code;
    } catch {
      /* kein JSON */
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
}

/** Der Anbieter hinter dieser Adresse – `'platform'`, wenn es die Plattform selbst ist (Schaufenster), `null`, wenn unbekannt. */
export async function fetchTenant(): Promise<TenantInfo | null | 'platform'> {
  try {
    return await request<TenantInfo>('/api/tenant');
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) return error.code === 'platform' ? 'platform' : null;
    throw error;
  }
}

export const fetchMe = () => request<{ loggedIn: boolean; name?: string | null; email?: string }>('/api/me');
export const requestLogin = (email: string) => request<{ ok: true; devLink?: string; devCode?: string }>('/api/auth/request', { method: 'POST', body: JSON.stringify({ email }) });
export const redeemToken = (token: string) => request<{ ok: true; name: string | null; email: string }>(`/api/auth/token/${encodeURIComponent(token)}`);
export const redeemCode = (email: string, code: string) => request<{ ok: true; name: string | null; email: string }>('/api/auth/code', { method: 'POST', body: JSON.stringify({ email, code }) });
export const logout = () => request<{ ok: true }>('/api/auth/logout', { method: 'POST' });

export const fetchLibrary = () => request<LibraryView>('/api/library');
export const fetchCourse = (slug: string) => request<CourseView>(`/api/courses/${encodeURIComponent(slug)}`);
export const fetchLesson = (slug: string, number: number) => request<LessonView>(`/api/courses/${encodeURIComponent(slug)}/lessons/${number}`);
export const reportProgress = (lessonId: string, positionSeconds: number, playing: boolean) =>
  request<{ watchedPercent: number; completed: boolean; takenOver: boolean }>(`/api/lessons/${lessonId}/progress`, { method: 'POST', body: JSON.stringify({ positionSeconds, playing }) });
export const setCompleted = (lessonId: string, completed: boolean) => request<{ completed: boolean }>(`/api/lessons/${lessonId}/complete`, { method: 'POST', body: JSON.stringify({ completed }) });
export const saveNote = (lessonId: string, body: string) => request<{ ok: true }>(`/api/lessons/${lessonId}/note`, { method: 'PUT', body: JSON.stringify({ body }) });
